import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { useLanguage } from '../../i18n';
import expenseService from '../../services/expenseService';
import { useIsMobile } from '../../hooks/useIsMobile';
import { userErrorMessage } from '../../utils/userError';
import { detailCategoriesFor } from '../../utils/financeCharts';
import Modal from '../ui/Modal';

// ─────────────────────────────────────────────────────────────────────────────
// Interactive category (#483) — the transactions behind ONE pie slice.
//
// Opened by clicking/tapping a slice or its legend row in FinanceCharts. The
// list comes from GET /api/expenses/analytics/category with the SAME scope the
// chart was drawn from (book / Unassigned, the chart's own date range, viewer
// currency) plus the pie's direction and the slice's label(s) — "Other" sends
// every folded label — so the server selects exactly the rows the slice summed,
// each at its converted amount in the chart currency.
//
// Shape (#479 rules, via <Modal>): portaled to <body> (a panel is a containing block for
// `fixed` and clips), a `.pai-dialog-frame` bounded to the dynamic viewport
// whose `.pai-dialog-body` list scrolls while the header + X stay put. On the
// mobile shell it is a bottom sheet (full width, anchored to the bottom edge)
// so the X and the list stay reachable. Closes on X, Escape (capture phase, so
// the panel behind does not close too) and a scrim click. Closing touches
// nothing in the charts — no state, no re-fetch, no scroll — and returns focus
// to the slice that opened it.
// ─────────────────────────────────────────────────────────────────────────────

const parseIso = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

const FinanceCategoryDetail = ({
    slice,
    label,
    direction,
    bookKey,
    startDate,
    endDate,
    displayCurrency,
    currency,
    formatAmount,
    onClose,
}) => {
    const { t, language } = useLanguage();
    const isMobile = useIsMobile();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const reqRef = useRef(0);
    const closeRef = useRef(null);
    const categories = useMemo(() => detailCategoriesFor(slice), [slice]);

    const load = useCallback(async () => {
        const seq = ++reqRef.current;
        setLoading(true);
        setError(null);
        try {
            const res = await expenseService.analyticsCategory({
                bookId: bookKey,
                startDate,
                endDate,
                displayCurrency,
                direction,
                categories,
            });
            if (seq !== reqRef.current) return;
            setDetail(res);
        } catch (err) {
            if (seq !== reqRef.current) return;
            setError(err || new Error('error'));
        } finally {
            if (seq === reqRef.current) setLoading(false);
        }
    }, [bookKey, startDate, endDate, displayCurrency, direction, categories]);

    useEffect(() => { load(); }, [load]);

    // Focus the X on open; give focus back to the slice/legend row on close.
    useEffect(() => {
        const opener = typeof document !== 'undefined' ? document.activeElement : null;
        closeRef.current?.focus?.({ preventScroll: true });
        return () => {
            if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
                opener.focus({ preventScroll: true });
            }
        };
    }, []);

    const unit = detail?.currency || currency;
    const total = detail ? detail.total : slice?.amount;
    const count = detail ? detail.count : slice?.count;
    const rows = detail?.transactions || [];

    const locale = language === 'es' ? 'es' : 'en';
    const dateFmt = useMemo(() => {
        try { return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return null; }
    }, [locale]);
    const fmtDate = (iso) => {
        const d = parseIso(iso);
        return dateFmt && d ? dateFmt.format(d) : iso;
    };
    const rangeText = startDate && endDate ? `${fmtDate(startDate)} – ${fmtDate(endDate)}` : '';
    const directionText = direction === 'income' ? t('finIncomePlural', 'Income') : t('finExpensesPlural', 'Expenses');

    return (
        <Modal
            onClose={onClose}
            zIndex="z-[70]"
            layout="column"
            placement={isMobile ? 'bottom' : 'center'}
            size={isMobile ? 'full' : 'lg'}
            surface={isMobile
                ? 'bg-pai-modal border border-pai rounded-t-modal shadow-modal'
                : undefined}
            cardClassName={isMobile ? 'pb-[env(safe-area-inset-bottom)]' : ''}
            labelledBy="finance-category-detail-title"
            scrimTestId="finance-category-detail-scrim"
            cardTestId="finance-category-detail"
            cardProps={{
                'data-variant': isMobile ? 'sheet' : 'dialog',
                'data-direction': direction,
                'data-category': slice?.category || '',
                'data-loading': loading ? 'true' : 'false',
                'data-count': detail ? detail.count : '',
                'data-total': detail ? detail.total : '',
                'data-currency': unit || '',
            }}
        >
                <header className="flex items-start gap-3 px-5 pt-5 pb-3.5 border-b border-pai-hairline flex-shrink-0 min-w-0">
                    <div className="min-w-0 flex-1">
                        <p className="pai-eyebrow truncate">
                            {directionText}{rangeText ? ` · ${rangeText}` : ''}
                        </p>
                        <h3
                            id="finance-category-detail-title"
                            data-testid="finance-category-detail-title"
                            className="mt-1 text-[18px] font-bold text-white truncate"
                            title={label}
                        >
                            {label}
                        </h3>
                        <p data-testid="finance-category-detail-summary" className="mt-0.5 text-[13px] text-pai-meta tnum truncate">
                            <span className="font-bold text-white">{formatAmount(total ?? 0, unit)}</span>
                            {' · '}
                            {t('finCatDetailCount', '{n} transaction(s)', { n: count ?? 0 })}
                        </p>
                    </div>
                    <button
                        ref={closeRef}
                        type="button"
                        data-testid="finance-category-detail-close"
                        aria-label={t('commonClose', 'Close')}
                        title={t('commonClose', 'Close')}
                        onClick={onClose}
                        className="pai-tap-row flex-shrink-0 w-9 h-9 -mr-1.5 -mt-1 flex items-center justify-center rounded-ctl text-pai-meta hover:text-white hover:bg-white/[0.08] transition-colors"
                    >
                        <X size={18} />
                    </button>
                </header>

                <div data-testid="finance-category-detail-list" className="pai-dialog-body px-2 py-2">
                    {loading && !detail && (
                        <div className="flex items-center gap-2 px-3 py-6 text-[13.5px] text-pai-meta">
                            <Loader2 size={14} className="animate-spin" />
                            {t('finLoadingEllipsis', 'Loading…')}
                        </div>
                    )}
                    {error && (
                        <div data-testid="finance-category-detail-error" className="m-2 border border-[rgba(240,68,56,0.32)] bg-[rgba(240,68,56,0.04)] rounded-row px-4 py-3 text-[13.5px] text-pai-red break-words">
                            {userErrorMessage(error, t)}{' '}
                            <button type="button" onClick={load} className="underline hover:text-white">
                                {t('commonRetry', 'Retry')}
                            </button>
                        </div>
                    )}
                    {detail && !error && rows.length === 0 && (
                        <p data-testid="finance-category-detail-empty" className="px-3 py-6 text-[13.5px] text-pai-meta break-words">
                            {t('finCatDetailEmpty', 'No transactions in this category for this period.')}
                        </p>
                    )}
                    {detail && !error && detail.unconverted_count > 0 && (
                        <p data-testid="finance-category-detail-unconverted" className="px-3 pb-2 text-[12.5px] text-pai-amber break-words">
                            {t('finVizUnconverted', '{n} transaction(s) left out: no exchange rate was available.', { n: detail.unconverted_count })}
                        </p>
                    )}
                    {detail && !error && rows.length > 0 && (
                        <ul className="space-y-0.5">
                            {rows.map((row) => {
                                const foreign = row.original_currency && row.original_currency !== row.currency;
                                return (
                                    <li
                                        key={row.id}
                                        data-testid="finance-category-detail-row"
                                        data-id={row.id}
                                        data-date={row.date}
                                        data-amount={row.amount}
                                        data-currency={row.currency}
                                        className="flex items-center gap-3 px-3 py-2.5 rounded-seg min-w-0 hover:bg-white/[0.04]"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p data-testid="finance-category-detail-row-date" className="text-[11.5px] leading-tight text-pai-faint tnum">
                                                {fmtDate(row.date)}
                                            </p>
                                            <p
                                                data-testid="finance-category-detail-row-title"
                                                className="mt-0.5 text-[14px] font-medium text-pai-secondary truncate"
                                                title={row.title || undefined}
                                            >
                                                {row.title || t('finCatDetailUntitled', 'Untitled transaction')}
                                            </p>
                                        </div>
                                        <div className="flex-shrink-0 text-right">
                                            <p data-testid="finance-category-detail-row-amount" className="text-[14px] font-bold text-white tnum whitespace-nowrap">
                                                {formatAmount(row.amount, row.currency)}
                                            </p>
                                            {foreign && (
                                                <p className="text-[11.5px] text-pai-faint tnum whitespace-nowrap">
                                                    {formatAmount(row.original_amount, row.original_currency)}
                                                </p>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </Modal>
    );
};

export default FinanceCategoryDetail;
